import { FC, useState } from "react"
import { useTranslation } from "react-i18next"

import { Grid, GridItem } from "components/Grid"
import ListKeyValue from "components/List/ListKeyValue"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import NoValue from "components/NoValue/NoValue"
import Text from "components/Text/Text"
import Checkbox from "components/Checkbox/Checkbox"
import TextSection from "components/TextSection/TextSection"
import Button from "components/Button/Button"
import ShippingInstructionsModal from "components/ShippingInstructionsModal/ShippingInstructionsModal"
import { Dayjs } from "utils/dayjs"

interface IShippingOrderModalBasicDataProps {
    deliveryTermDescription?: string
    responsibleWarehouseDescription?: string
    departureDate?: Dayjs | null
    departureDateMin?: Dayjs | null
    arrivalDate?: Dayjs | null
    arrivalDateMax?: Dayjs | null
    personInCharge?: string | null
    personInChargePhone?: string
    note?: string
    deliveryNoteHeaderText?: string
    deliveryNoteFooterText?: string
    deliveryNotePrintFeatures: boolean
    showDeliveryNoteTextSection?: boolean
}

const ShippingOrderModalBasicData: FC<IShippingOrderModalBasicDataProps> = ({
    deliveryTermDescription,
    responsibleWarehouseDescription,
    departureDate,
    departureDateMin,
    arrivalDate,
    arrivalDateMax,
    personInCharge,
    personInChargePhone,
    note,
    deliveryNoteHeaderText,
    deliveryNoteFooterText,
    deliveryNotePrintFeatures,
    showDeliveryNoteTextSection = false,
}) => {
    const { t } = useTranslation()
    const [showShippingInstructionsModal, setShowShippingInstructionsModal] = useState(false)

    function renderDeliveryNoteTextSection({
        title,
        text,
        openViewModal,
        textBlockRef,
    }: {
        title: string
        text?: string
        openViewModal?: () => void
        textBlockRef?: React.RefObject<HTMLDivElement | null>
    }) {
        return (
            <>
                <div className="mb-1">{title}</div>
                {text
                    ? <div className="clamp-2 white-space-pre-wrap" ref={textBlockRef}>
                        {text}
                    </div>
                    : <NoValue>{t('common.noText')}</NoValue>
                }
                {!!openViewModal &&
                    <div className="mt-1">
                        <Button
                            theme="link"
                            onClick={openViewModal}
                            label={t('reservation.showText')}
                        />
                    </div>
                }
            </>
        )
    }

    const departureDateDiffers = !!departureDate && !!departureDateMin && !departureDate.isSame(departureDateMin)
    const arrivalDateDiffers = !!arrivalDate && !!arrivalDateMax && !arrivalDate.isSame(arrivalDateMax)

    return (
        <>
            <Grid columns="minmax(0, 1fr) minmax(0, 1fr)">
                <GridItem className="pr-4 border-right-grey-100">
                    <ListKeyValue
                        items={[
                            {
                                label: t('shippingOrders.termsOfDelivery'),
                                value: deliveryTermDescription ? <FlexibleContent text={deliveryTermDescription} /> : <NoValue />
                            },
                            {
                                label: t('shippingOrders.responsibleWarehouse'),
                                value: responsibleWarehouseDescription ? <FlexibleContent text={responsibleWarehouseDescription} /> : <NoValue />
                            },
                            {
                                label: t('shippingOrders.departure'),
                                value: <>
                                    {(departureDateMin || departureDate)?.format(t('config.dateLongTimeFormat')) ?? null}
                                    {departureDateDiffers && <Text type="pSmallest" className="color-grey-500">
                                        {t('shippingOrders.initialDate')}: {departureDate?.format(t('config.dateLongTimeFormat'))}
                                    </Text>}
                                </>,
                            },
                            {
                                label: t('shippingOrders.arrival'),
                                value: <>
                                    {(arrivalDateMax || arrivalDate)?.format(t('config.dateLongTimeFormat')) ?? null}
                                    {arrivalDateDiffers && <Text type="pSmallest" className="color-grey-500">
                                        {t('shippingOrders.initialDate')}: {arrivalDate?.format(t('config.dateLongTimeFormat'))}
                                    </Text>}
                                </>,
                            },
                            {
                                label: t('reservation.contactPersonAtDeliveryAddress'),
                                value: personInCharge || <NoValue />
                            },
                            {
                                label: t('reservation.contactPersonAtDeliveryAddressPhone'),
                                value: personInChargePhone || <NoValue />
                            },
                        ]}
                    />
                </GridItem>
                <GridItem className="pl-4">
                    <div className="font-bold mt-1-5 mb-2">
                        {t('shippingOrders.shippingInstructions')}
                    </div>
                    <TextSection
                        text={note ?? ""}
                        renderContent={({ openViewModal, textBlockRef }) => (<>
                            {note
                                ? <div className="clamp-5 white-space-pre-wrap" ref={textBlockRef}>
                                    {note}
                                </div>
                                : <NoValue>{t('reservation.noInformation')}</NoValue>
                            }
                            {!!openViewModal &&
                                <div className="mt-1">
                                    <Button
                                        theme="link"
                                        onClick={() => setShowShippingInstructionsModal(true)}
                                        label={t('prepareShipping.showShippingInstructions')}
                                    />
                                </div>
                            }
                        </>)}
                    />
                </GridItem>
            </Grid>
            {showDeliveryNoteTextSection && <>
                <Text type="h4" className="border-top-grey-100 mt-4 mb-3 pt-3">{t('reservation.deliveryNoteText')}</Text>
                <Grid columns="minmax(0, 1fr) minmax(0, 1fr)">
                    <GridItem className="pr-4 border-right-grey-100">
                        <TextSection
                            popupTitle={t('reservation.deliveryNoteHeaderSectionPopupTitle')}
                            text={deliveryNoteHeaderText ?? ""}
                            formattingRuleLabel={t("textModal.noRule")}
                            renderContent={params => renderDeliveryNoteTextSection({
                                title: t('reservation.headerSection'),
                                text: deliveryNoteHeaderText,
                                openViewModal: params.openViewModal,
                                textBlockRef: params.textBlockRef,
                            })}
                        />
                    </GridItem>
                    <GridItem className="pl-4">
                        <TextSection
                            popupTitle={t('reservation.deliveryNoteFooterSectionPopupTitle')}
                            text={deliveryNoteFooterText ?? ""}
                            formattingRuleLabel={t("textModal.noRule")}
                            renderContent={params => renderDeliveryNoteTextSection({
                                title: t('reservation.footerSection'),
                                text: deliveryNoteFooterText,
                                openViewModal: params.openViewModal,
                                textBlockRef: params.textBlockRef,
                            })}
                        />
                    </GridItem>
                </Grid>
                <div className="mt-4">
                    <Checkbox
                        label={t('shippingOrders.printFeaturesOnDeliveryNote')}
                        checked={deliveryNotePrintFeatures}
                        disabled
                        testId="print-features-on-delivery-note-checkbox"
                    />
                </div>
            </>}
            {showShippingInstructionsModal &&
                <ShippingInstructionsModal
                    shippingInstructions={note}
                    onClose={() => setShowShippingInstructionsModal(false)}
                />
            }
        </>
    )
}

export default ShippingOrderModalBasicData
